import { useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, CircularProgress, Grid } from "@mui/material"
import {
    SelectedPriceListItem,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { ProductContainer } from "./product/ProductContainer"
import { MeetingServiceConfig } from "../form/formTypes"
import { ShopListHeader } from "./header/ShopListHeader"
import { filterShops } from "./helpers"
import {
    fetchProducts,
    FetchProductsResponse,
    MerchantProductsQuery,
} from "../../../../../modules/merchant/admin/products/productsApi"
import { NoProductsDialog } from "./NoProductsDialog"

const useStyles = makeStyles()(() => ({
    container: {
        display: "grid",
        maxHeight: "100%",
        gridTemplateRows: "auto 1fr",
        overflow: "hidden",
        width: "100%",
    },
}))
export type ShopFiltersTypes = Readonly<{
    isFavorite: boolean
    search: string
    isListView: boolean
}>

type ShopListProps = Readonly<{
    priceList: SelectedPriceListItem
    meetingServiceConfiguration?: MeetingServiceConfig
    handleCloseMainDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    handleOpenSelectPriceListsDialog: () => void
    dialogType: CreateOrderDialogType
    user: SelectedMainUserState
}>

const transformProductsData = (data: FetchProductsResponse) => {
    return data.productGroups || []
}

export function ShopList({
    priceList,
    meetingServiceConfiguration,
    handleCloseMainDialog,
    flowConfig,
    handleOpenSelectPriceListsDialog,
    dialogType,
    user,
}: ShopListProps) {
    const { classes } = useStyles()
    const [isDialogOpen, setDialogOpen] = useState(false)
    const [filters, setFilters] = useState<ShopFiltersTypes>({ isFavorite: false, search: "", isListView: false })
    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    const isRefundOrder = dialogType === CreateOrderDialogType.CREDIT_NOTE

    const {
        data: products = [],
        isLoading,
        isError,
    } = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: priceList.merchantId,
            priceListId: priceList.priceListId,
            isActive: true,
        },
        select: transformProductsData,
    })

    const filteredShops = useMemo(
        () => filterShops(filters.isFavorite, searchRegex, products),
        [products, filters.isFavorite, searchRegex],
    )

    useEffect(() => {
        if ((!isLoading && products.length === 0) || isError) {
            !isRefundOrder && setDialogOpen(true)
        }
    }, [isLoading, products, isError, isRefundOrder])

    const handleCloseDialog = () => {
        setDialogOpen(false)
    }

    if (isLoading) {
        return (
            <Grid container alignItems="center" justifyContent="center" style={{ height: "100%" }}>
                <CircularProgress size={70} />
            </Grid>
        )
    }

    if (isDialogOpen) {
        return (
            <NoProductsDialog
                open={isDialogOpen}
                handleCloseDialog={handleCloseDialog}
                handleCloseMainDialog={handleCloseMainDialog}
                handleOpenSelectPriceListsDialog={
                    !priceList.isOnlyOnePriceList ? handleOpenSelectPriceListsDialog : undefined
                }
                isError={isError}
            />
        )
    }

    return (
        <Box className={classes.container}>
            <ShopListHeader
                filters={filters}
                setFilters={setFilters}
                meetingServiceConfiguration={meetingServiceConfiguration}
                handleCloseMainDialog={handleCloseMainDialog}
                flowConfig={flowConfig}
                dialogType={dialogType}
                user={user}
            />
            <ProductContainer
                list={filteredShops}
                searchRegex={searchRegex}
                isListView={filters.isListView}
                isFavoriteFilter={filters.isFavorite}
                handleCloseMainDialog={handleCloseMainDialog}
                isDisabledContent={isRefundOrder}
            />
        </Box>
    )
}
